import React from "react";
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, ReferenceLine, Tooltip } from "recharts";

// A minimal trend sparkline: gradient-filled area, no axes/labels. Pin the Y domain to the
// data range (not the recharts default [0, dataMax]) so the line shows the *shape* of the trend
// rather than its magnitude — otherwise values like 10000→10064 render as a flat block.
// `fromZero` opts out of that, for a series that is also drawn full size somewhere else and has
// to keep the same shape there — sessions a day on the Monitored URI chart, for instance.
// `color2` draws a second series from `v2` — the redirect destination's sessions on the Monitored
// URI chart, which uses the same two colours. `average` draws `avg` dashed over the top and drops the
// daily line to a faint band behind it — the same 7-day mean, and the same arrangement, as the
// Monitored URI chart. At this size the daily line on its own is unreadable.
// `markers` draws a dashed vertical at each listed `day` — the day a page broke, on the Issues rows —
// over the points, so it stays visible when a point falls on the same day.
// `points` draws a solid vertical in its own `color` at each `{ day, color }` — a pivot. With
// `band: [from, to]` it covers only that share of the height, so points on the same day stay apart.
// `scale` shows the y axis with two ticks, 0 and the highest value, so rows can be compared; it only
// makes sense with `fromZero`.
// `tooltip` is a component given the hovered `day` and its `v`, shown as the chart's hover box.
// `type="monotone"` so the curve never overshoots its own data and gets clipped by the box.
const AVERAGE_DAYS = 7;

const compact = new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 });

/** Daily values with the trailing 7-day mean over them, as `average` draws it. */
export const withTrailingMean = (daily, days) =>
    daily.map((value, index) => {
        const window = daily.slice(Math.max(0, index + 1 - AVERAGE_DAYS), index + 1);
        return { day: days[index], v: value, avg: window.reduce((total, each) => total + each, 0) / window.length };
    });

export default function Sparkline({ data, color, color2, average, fromZero, markers, points, scale, tooltip: HoverBox }) {
    // Need 2+ real (non-null) points — gap days carry `v: null` so an array of length 30
    // can still be all-gaps. Without this, a single real point would render as nothing
    // visible (dot={false}) inside an otherwise sized box.
    const realPoints = (data || []).reduce((n, d) => n + (typeof d?.v === "number" ? 1 : 0), 0);
    if (realPoints < 2) return null;
    const id = `spark-${color.replace("#", "")}`;
    const top = Math.max(...data.map((d) => d.v ?? 0));
    const id2 = color2 ? `spark-${color2.replace("#", "")}` : null;
    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={scale ? { top: 6, right: 0, bottom: 5, left: 0 } : { top: 3, right: 0, bottom: 0, left: 0 }}>
                <defs>
                    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                    {id2 && (
                        <linearGradient id={id2} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color2} stopOpacity={0.2} />
                            <stop offset="100%" stopColor={color2} stopOpacity={0} />
                        </linearGradient>
                    )}
                </defs>
                {scale
                    ? <YAxis domain={[0, "dataMax"]} ticks={[0, top]} interval={0}
                             tickFormatter={(value) => compact.format(value)} tick={{ fontSize: 9, fill: "#94a3b8" }}
                             axisLine={false} tickLine={false} width={28} />
                    : <YAxis hide domain={fromZero ? [0, "dataMax"] : ["dataMin", "dataMax"]} />}
                {(markers || points || HoverBox) && <XAxis dataKey="day" hide />}
                {HoverBox && (
                    <Tooltip cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }} isAnimationActive={false}
                             content={({ active, payload }) => active && payload?.length ? <HoverBox {...payload[0].payload} /> : null} />
                )}
                {id2 && (
                    <Area type="monotone" dataKey="v2" stroke={color2} strokeWidth={1.5}
                          fill={`url(#${id2})`} dot={false} isAnimationActive={false} connectNulls />
                )}
                <Area type="monotone" dataKey="v" stroke={average ? "#a5b4fc" : color} strokeWidth={average ? 1 : 1.5}
                      fill={`url(#${id})`} dot={false} isAnimationActive={false} connectNulls />
                {average && (
                    <Line type="monotone" dataKey="avg" stroke={average} strokeWidth={1.75}
                          strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls />
                )}
                {(points || []).map(({ day, color: pointColor, band }) => (band
                    ? <ReferenceLine key={day + pointColor} segment={[{ x: day, y: top * band[0] }, { x: day, y: top * band[1] }]}
                                     stroke={pointColor} strokeWidth={2.5} ifOverflow="hidden" />
                    : <ReferenceLine key={day + pointColor} x={day} stroke={pointColor} strokeWidth={1.5} ifOverflow="hidden" />
                ))}
                {(markers || []).map((day) => (
                    <ReferenceLine key={day} x={day} stroke="#e0776f" strokeWidth={1.5} strokeDasharray="3 2" ifOverflow="hidden" />
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
}
